<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';

import { reactive, ref } from 'vue';

import KRadioField from '@/components/fields/KRadioField.vue';
import KSingleAutocompleteField from '@/components/fields/KSingleAutocompleteField.vue';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';
import { splitToBaseItems } from '@/constants/fieldItems';

type StopNumber = 2 | 3;

export interface InitialData {
  stopNumber?: StopNumber;
  warehouse?: string;
}

export interface ValidData {
  stopNumber: StopNumber;
  warehouse: string;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
  stopsCount?: number;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
  stopsCount: 2,
});

const emit = defineEmits<{
  submit: [event: SubmitEventPromise | null | undefined, formData: ValidData];
}>();

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, function (event) {
  if (formData.stopNumber == null) return;
  if (formData.warehouse == null) return;

  emit('submit', event, {
    stopNumber: formData.stopNumber,
    warehouse: formData.warehouse,
  });
});
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    :class="$style.jobEditSplitToBaseDialogForm"
    validate-on="lazy"
    data-testid="jobEditSplitToBaseDialogForm"
    @submit.prevent="handleSubmit"
  >
    <v-radio-group
      v-model="formData.stopNumber"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      inline
    >
      <KRadioField label="Stop 2" :value="2" />
      <KRadioField
        label="Stop 3"
        :value="3"
        :disabled="props.stopsCount === 2"
      />
    </v-radio-group>

    <KSingleAutocompleteField
      v-model="formData.warehouse"
      label="Warehouse"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :items="splitToBaseItems"
      @keydown.enter="handleSubmit"
    />
    <input type="submit" class="hidden" :disabled="props.loading" />
  </v-form>
</template>

<style lang="scss" module>
.jobEditSplitToBaseDialogForm {
  display: contents;
}
</style>
